import { GeneratedPodcastStageStatus } from '@kimuno/api-sdk/lib/types/responses/Ai';
import { Alert, AlertTitle, Box, SvgIcon, Typography } from '@mui/material';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import { getCurrentConversation } from '../../../reducers/aiAssistant.selectors';
import services from '../../../services';
import { aiAssistantEditorContext } from '../../../state/AIAssistantEditorProvider';
import getMuiIcon from '../../../utils/getMuiIcon';
import { convertMarkdownToHtml } from '../../utils/contentConverter';
import AIAssistantTextEditorCopyButton from '../shared/TextEditor/AIAssistantTextEditorCopyButton';
import AIAssistantTextEditorExportButton, {
	type TextEditorDownloadFormat,
} from '../shared/TextEditor/AIAssistantTextEditorExportButton';
import usePodcastAudioActionTracking from '../shared/usePodcastAudioActionTracking';
import PodcastAudioPlayer from './PodcastAudioPlayer';

const containerStyles = {
	display: 'flex',
	flexDirection: 'column',
	gap: spaces.lg,
	padding: spaces.lg,
	width: '100%',
	boxSizing: 'border-box',
};

const transcriptHeaderStyles = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: spaces.sm,
	flexWrap: 'wrap',
};

const transcriptTitleStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: spaces.xs,
};

const transcriptActionsStyles = {
	display: 'flex',
	alignItems: 'center',
	gap: spaces.sm,
};

const transcriptCardStyles = {
	width: '100%',
	padding: spaces.md,
	border: `1px solid ${palette.divider}`,
	borderRadius: borderRadius.md,
	background: palette.background.paperElevation2,
	boxSizing: 'border-box',
};

const PodcastDetails: React.FC = () => {
	const { t } = useTranslation();
	const {
		podcastDetails,
		autoPlayPodcastId,
		consumeAutoPlayPodcast,
		podcastPauseRequested,
		consumePodcastPauseRequest,
		reportPodcastPlaybackState,
	} = useContext(aiAssistantEditorContext);
	const currentConversation = useSelector(getCurrentConversation);
	const currentConversationId = currentConversation?.data?.id;
	const { handleAudioPlayTrack, handleAudioPauseTrack } = usePodcastAudioActionTracking(
		podcastDetails?.id
	);

	const transcriptHtml = useMemo(
		() => (podcastDetails ? convertMarkdownToHtml(podcastDetails.transcript) : ''),
		[podcastDetails]
	);

	const trackTranscriptExport = (
		exportType: 'download' | 'print',
		exportFormat: TextEditorDownloadFormat
	) => {
		if (!currentConversationId) return;
		services.track.ampliTrackTextEditorFileExportedEvent({
			conversationId: currentConversationId,
			exportType,
			exportFormat,
			feature: ['podcast generation'],
		});
	};

	const handleTranscriptDownload = (format: TextEditorDownloadFormat) => {
		trackTranscriptExport('download', format);
	};

	const handleTranscriptPrint = () => {
		trackTranscriptExport('print', 'pdf');
	};

	if (!podcastDetails) {
		return null;
	}

	// Skip empty parts (pre-params legacy podcasts, or an API not yet returning
	// the metadata fields) so the row never renders as bare separators (WOR-977).
	const metadataLabel = [
		podcastDetails.subject,
		podcastDetails.grade,
		podcastDetails.language,
		podcastDetails.format,
	]
		.filter(Boolean)
		.join(' • ');

	const showAudioError = Boolean(podcastDetails.audioGenerationFailed) || !podcastDetails.audioUrl;
	const shouldAutoPlay =
		// The id (not a boolean) guarantees a switch intent can never fire against the
		// previous podcast's details, which linger until the fetch resolves.
		autoPlayPodcastId === podcastDetails.id &&
		podcastDetails.audioStatus === GeneratedPodcastStageStatus.READY &&
		// defensive: the player isn't rendered on error, but keep the gate self-consistent.
		!showAudioError;

	return (
		<Box sx={containerStyles} data-testid="podcast-details">
			{metadataLabel && (
				<Typography
					variant="body2"
					sx={{ color: palette.text.secondary }}
					data-testid="podcast-details-metadata"
				>
					{metadataLabel}
				</Typography>
			)}

			{showAudioError ? (
				<Alert severity="warning" data-testid="podcast-audio-error-alert" sx={{ zIndex: 'auto' }}>
					<AlertTitle sx={{ color: palette.warning.main }}>{t('Audio could not be loaded')}</AlertTitle>
					<Typography variant="body2" sx={{ color: palette.warning.main }}>
						{t('The audio file could not be loaded. However, the transcript is still available to you.')}
					</Typography>
				</Alert>
			) : (
				<PodcastAudioPlayer
					src={podcastDetails.audioUrl}
					durationLabelFallback={podcastDetails.durationLabel}
					onPlay={handleAudioPlayTrack}
					onPause={handleAudioPauseTrack}
					autoPlay={shouldAutoPlay}
					onAutoPlayConsumed={consumeAutoPlayPodcast}
					pauseRequested={podcastPauseRequested}
					onPauseRequestConsumed={consumePodcastPauseRequest}
					onPlaybackStateChange={reportPodcastPlaybackState}
				/>
			)}

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.sm }}>
				<Box sx={transcriptHeaderStyles}>
					<Box sx={transcriptTitleStyles}>
						<SvgIcon
							component={getMuiIcon('transcript')}
							aria-hidden="true"
							sx={{ color: palette.text.secondary }}
						/>
						<Typography variant="subtitle1" sx={{ color: palette.text.primary }}>
							{t('Transcript')}
						</Typography>
					</Box>
					<Box sx={transcriptActionsStyles}>
						<AIAssistantTextEditorCopyButton
							content={transcriptHtml}
							dataTestId="podcast-details-copy-transcript"
							sx={{ color: palette.primary.main }}
						/>
						<AIAssistantTextEditorExportButton
							variant="outlined"
							label={t('Export transcript')}
							id="podcast-details-export-transcript-button"
							dataTestId="podcast-details-export-transcript"
							showShare={false}
							showNotification={false}
							menuAnchorOrigin="right"
							content={transcriptHtml}
							onDownload={handleTranscriptDownload}
							onPrint={handleTranscriptPrint}
						/>
					</Box>
				</Box>

				<Box sx={transcriptCardStyles}>
					<Typography
						variant="body2"
						component="div"
						sx={{ color: palette.text.primary, whiteSpace: 'pre-wrap' }}
					>
						{podcastDetails.transcript}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

export default PodcastDetails;
